import React from "react";
import SupportQuestionMark from "../supportbeacon/SupportQuestionMark";
import { formatShortDate, getCurrency } from "../utilities";
import { OrderableTable, type TableColumn, type OrderableTableFooter } from "components/Tables";
import moment from "moment";
import { formatMoneyDto } from "utilities/formatter";

interface Props {
  onEditClicked: (product: server.dto.Product) => void
  onDeleteClicked: (product: server.dto.Product) => void
  onCopyClicked: (product: server.dto.Product) => void
  onProductsOrderChanged: (productsByParent: server.dto.ProductsByParent) => void
  productsPanelConfig: server.dto.LinkedProductPanelConfig
  productsByParent: server.dto.ProductsByParent
  disabled?: boolean
}

const renderExchangeRate = (products: server.dto.ProductsByParent) => (
  <div className="flex">
    <div className="margin-right">
      <SupportQuestionMark small white articleId={serverReference.supportArticleIds.exchangeRates} />
    </div>
    <div>
      {RESX.Financial.ExchangeRate} {getCurrency(products.currency).text} &gt; {getCurrency(products.baseCurrency).text} = {products.exchangeRate}
    </div>
  </div>
);

const renderProduct = (product: server.dto.Product) => (
  <>
    <div>
      {product.titleFormatted}
      {product.date && <span className="s1_gray" style={{ marginLeft: 5 }}>
        {formatShortDate(moment.utc(product.date))}
      </span>}
    </div>
    {product.description && <div className="text-sublabel">
      {product.description
        .split("\n")
        .map((d, idx) => <React.Fragment key={idx}>{d}<br /></React.Fragment>)}
    </div>}
    <div className="s1_smaller">
      {product.productType.value !== "undefined" && <span className="s1_gray">{product.productType.text}&nbsp;</span>}
      {product.productType.value === "undefined" &&
        <>
          <a onClick={(e) => {
            e.stopPropagation();
            (window).OpenSupportSidebar(serverReference.supportArticleIds.whatIsProductType);
          }} className="s1_red">{product.productType.text}</a>
          &nbsp;
        </>}
      <span className="s1_gray">| {product.ledgerNumber}</span>
    </div>
  </>
);

const displayVatPart = (vatPart: server.dto.MoneyCostsSales, productsPanelConfig: server.dto.LinkedProductPanelConfig) => {
  let display = false;

  if (productsPanelConfig.canReadCosts && vatPart.costs.amount !== 0) display = true;
  else if (productsPanelConfig.canReadSales && vatPart.sales.amount !== 0) display = true;

  return display;
};

const getFooter = (products: server.dto.ProductsByParent, productsPanelConfig: server.dto.LinkedProductPanelConfig) => {
  const footers: OrderableTableFooter[] = [];
  if (products.subtotal) {
    footers.push({
      key: "subtotal",
      entries: [RESX.Financial.Subtotal, null, products.subtotal.costs?.formatted, null, products.subtotal.sales?.formatted]
    });
  };
  products.vatParts.map((vatPart, idx) => (displayVatPart(vatPart, productsPanelConfig) && footers.push({
    key: `vatPart${idx}`,
    entries: [vatPart.title, null, vatPart.costs?.formatted, null, vatPart.sales?.formatted]
  })));
  footers.push({
    key: "total",
    className: "dark",
    entries: [RESX.Financial.Total, null, products.total.costs?.formatted, null, products.total.sales?.formatted]
  });
  if (products.baseCurrency !== products.currency) {
    footers.push({
      key: "exchangeRate",
      className: "blue-bg white-text",
      entries: [renderExchangeRate(products), null, products.total.costs?.formattedBaseCurrencyAmount, null, products.total.sales?.formattedBaseCurrencyAmount]
    });
  }
  return footers;
};

const ProductsTable = (props: Props) => {
  const orderChanged = (products: server.dto.Product[]) => {
    const newProductsByParent: server.dto.ProductsByParent = {
      ...props.productsByParent,
      collection: products
    };

    props.onProductsOrderChanged(newProductsByParent);
  };

  const { onEditClicked, onDeleteClicked, onCopyClicked, productsPanelConfig, productsByParent: products } = props;

  const canModify = props.disabled ? false : productsPanelConfig.canModify;

  const columns: Array<TableColumn<server.dto.Product>> = [{
    key: "product",
    header: "",
    render: renderProduct
  }, {
    key: "costsVat",
    header: RESX.Financial.VAT,
    hidden: !productsPanelConfig.canReadCosts,
    width: 45,
    className: "s1_cell_financialamount",
    render: (product) => product.costs.totalExVat.amount !== 0 && product.costs.vatRate.percentageFormatted
  }, {
    key: "costsTotalExVat",
    header: RESX.Financial.Costs,
    hidden: !productsPanelConfig.canReadCosts,
    width: 100,
    className: "s1_cell_financialamount",
    render: (product) => product.costs.totalExVat.amount !== 0 && formatMoneyDto({ money: product.costs.totalExVat, displayCents: true })
  }, {
    key: "salesVat",
    header: RESX.Financial.VAT,
    hidden: !productsPanelConfig.canReadSales,
    width: 45,
    className: "s1_cell_financialamount",
    render: (product) => product.sales.totalExVat.amount !== 0 && product.sales.vatRate.percentageFormatted
  }, {
    key: "salesTotalExVat",
    header: RESX.Financial.Sales,
    hidden: !productsPanelConfig.canReadSales,
    width: 100,
    className: "s1_cell_financialamount",
    render: (product) => product.sales.totalExVat.amount !== 0 && formatMoneyDto({ money: product.sales.totalExVat, displayCents: true })
  }];

  return <OrderableTable
    items={products.collection}
    isLoading={false}
    disabled={!canModify}
    onEdit={onEditClicked}
    onDelete={onDeleteClicked}
    onCopy={onCopyClicked}
    getKey={product => `${product.productId}`}
    onOrderChanged={orderChanged}
    columns={columns}
    footers={getFooter(products, productsPanelConfig)}
  />;
};

export default ProductsTable;